import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { I18n } from 'react-redux-i18n';
import { MoreOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import moment from 'moment';
import { get } from 'lodash';

import Button from 'components/Button';
import CountryLabel from 'components/CountryLabel';
import Loader from 'components/Loader';
import Popup from 'components/Popup';
import Spinner from 'components/Spinner';
import Tag from 'components/Tag';
import SourceLink from 'modules/Media/components/SourceLink';
import ActionMenu from 'modules/Media/components/ActionMenu';
import ShareModal from 'modules/Media/components/ShareModal';
import TagsModal from 'modules/Media/components/TagsModal';
import { ArticleViewContainer } from 'modules/Media/pages/ArticleViewContainer';

import { usePostUpdateCache } from 'apiTyped/hooks/post.cache';
import { usePost } from 'apiTyped/hooks/post.query';
import { usePressHeader } from 'apiTyped/hooks/press.query';
import {
  usePostDownloadMutation,
  usePostSetFavoriteMutation,
  usePostSetReadMutation
} from 'apiTyped/hooks/post.mutations';
import { useUserMe } from 'apiTyped/hooks/users.query';
import { useDisclose } from 'utils/hooks/useDisclosure';
import { parseArticle, isHTML } from 'utils/parser';
import { translateArticle } from 'utils/translation';

const isMobile = window.innerWidth <= 845;

type ArticleViewProps = {
  match: {
    params: {
      // TODO: should be named post_id
      articleId: string;
    };
  };
};

const ArticleView = (props: ArticleViewProps) => {
  const { user } = useUserMe();

  // Set the post_id from the url
  const post_id = parseInt(props.match.params.articleId);

  // Local state
  const [showErrorTranslationModal, setShowErrorTranslationModal] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const modalActions = useDisclose();
  const tagsModal = useDisclose();
  const shareModal = useDisclose();

  // Queries
  const { isLoadingPost, post } = usePost(post_id);
  const { isLoadingPressHeader } = usePressHeader();

  // Mutations
  const postSetReadMutation = usePostSetReadMutation(post_id);
  const postSetFavoriteMutation = usePostSetFavoriteMutation(post_id);
  const postUpdateCache = usePostUpdateCache(post_id);
  const postDownloadPdfMutation = usePostDownloadMutation(post_id);

  // Mark article as read on first render
  useEffect(() => {
    postSetReadMutation.mutate();
  }, []);

  const handleTagsSubmit = async (
    _tagsToAdd: ({ id: number; name: string } | undefined)[],
    _tagsToRemove: ({ id: number; name: string } | undefined)[]
  ) => {
    tagsModal.onOpen();
    setIsLoading(true);
    setIsLoading(false);
  };

  const handleSetFavorite = async () => {
    setIsLoading(true);
    await postSetFavoriteMutation.mutateAsync();
    setIsLoading(false);
  };

  const handleTranslate = async () => {
    if (!post) {
      return;
    }
    setIsLoading(true);
    const translation = await translateArticle(
      post.content.title,
      post.content.body
    );
    // Update the post in cache with the new translation
    if (translation) {
      postUpdateCache((oldPost) =>
        oldPost
          ? {
              ...oldPost,
              content: {
                ...oldPost.content,
                title: translation.title,
                body: translation.body
              }
            }
          : oldPost
      );
    } else {
      setShowErrorTranslationModal(true);
    }
    setIsLoading(false);
  };

  const handleDownloadPdf = async () => {
    setIsFetching(true);
    const data = await postDownloadPdfMutation.mutateAsync();
    if (data) {
      const link = document.createElement('a');
      link.href = data.file_url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    setIsFetching(false);
  };

  // Could be moved to a separate component
  const RenderCountry = () => {
    const country = get(post, 'content.source.country_origin');
    if (!country) {
      return null;
    }

    return (
      <>
        <div className="article-side-menu-item">
          {!isMobile && (
            <p className="article-side-menu-item-label">
              {I18n.t('prices.filters.market')}
            </p>
          )}
          <div className="article-side-menu-item-content">
            <div className="article-market-container">
              <CountryLabel
                name={get(country, 'code')}
                city={get(country, 'name')}
                customStyles={`.country-label-flag{font-size:22px} .country-label-city{font-size:16px;margin-left:10px}`}
                style={undefined}
                styleFlag={undefined}
                country={undefined}
                styleLabel={undefined}
                onlyFlag={undefined}
              />
            </div>
          </div>
        </div>
      </>
    );
  };

  // Could be moved to a separate component
  const RenderSource = () => {
    const url = get(post, 'content.url');
    const source = get(post, 'content.source');
    if (!source) {
      return null;
    }
    return (
      <>
        <div className="article-side-menu-separator" />
        <div className="article-side-menu-item">
          <p className="article-side-menu-item-label">
            {I18n.t('media.articleView.source')}
          </p>
          <SourceLink
            link={url}
            title={source.name}
            showTooltip={false}
            area={undefined}
            digital={undefined}
          />
        </div>
      </>
    );
  };

  // Could be moved to a separate component
  const RenderTags = () => {
    const tags = get(post, 'tags');

    return (
      <>
        <div className="article-side-menu-separator" />
        <div className="article-side-menu-item">
          {!isMobile && (
            <p className="article-side-menu-item-label">
              {I18n.t('media.articleView.tags')}
            </p>
          )}
          <div className="tagListContainer">
            {tags &&
              tags.map((tag) => (
                <Tag
                  icon="close"
                  iconPosition="right"
                  onClickIcon={() => handleTagsSubmit([], [tag])}
                  key={tag.id}
                  label={tag.name}
                  selectable={false}
                  style={{
                    height: 25,
                    fontSize: 12,
                    backgroundColor: 'rgba(134, 142, 150, 0.1)',
                    color: '#2B3D54',
                    border: '1px solid rgba(134, 142, 150, 0.1)'
                  }}
                  onClick={() => {}}
                  active={undefined}
                  styleIcon={undefined}
                  styleIconContainer={undefined}
                  styleLabel={undefined}
                  image={undefined}
                />
              ))}
            {AddTags()}
          </div>
        </div>
      </>
    );
  };

  // Could be moved to a separate component
  const AddTags = () => {
    return (
      <div className="addTag">
        <Tag
          onClick={() => tagsModal.onOpen()}
          key="AddTag"
          label={I18n.t('addTag')}
          selectable={false}
          style={{
            height: 25,
            fontSize: 12,
            backgroundColor: 'white',
            color: '#2B3D54',
            border: 'solid 1px #2B3D54',
            fontWeight: 600
          }}
          active={undefined}
          styleIcon={undefined}
          styleIconContainer={undefined}
          styleLabel={undefined}
          image={undefined}
          icon={undefined}
          iconPosition={undefined}
          onClickIcon={undefined}
        />
      </div>
    );
  };

  // Could be moved to a separate component
  const RenderActionMenu = () => {
    if (!post) {
      return null;
    }

    const url = get(post, 'content.url');
    const is_favorite = get(post, 'is_favorite');
    const languageClient = get(user, 'language', 'FR').toUpperCase();
    let languageArticle = get(
      post,
      'content.source.country_origin.code',
      ''
    ).toUpperCase();
    if (languageArticle === 'US') languageArticle = 'EN';
    const translateAction =
      languageArticle != languageClient ? handleTranslate : null;

    if (!isMobile) {
      return (
        <>
          <div className="article-side-menu-separator" />
          <div className="article-side-menu-item">
            <ActionMenu
              isFavorite={is_favorite}
              onClickFavorite={handleSetFavorite}
              originalContent={url}
              onClickRead={null}
              onClickTag={null}
              onClickShare={() => shareModal.onOpen()}
              onClickDlPdf={handleDownloadPdf}
              onClickTranslate={translateAction}
            />
          </div>
        </>
      );
    }

    return (
      <div className="article-footer-menu-actions">
        <div>
          <Button
            label={
              <div>
                <MoreOutlined className="action-button-icon" />
                <p className="action-button-label actions">
                  {I18n.t(
                    'media.selectionArticles.actionButtonLabel'
                  ).toUpperCase()}
                </p>
              </div>
            }
            icon={null}
            color="#2b3d54"
            onClick={() => {
              if (modalActions.isOpen) modalActions.onClose();
              else modalActions.onOpen();
            }}
            fontFamily="Oswald"
            className="header-link actions-button"
          />
          <Modal
            open={modalActions.isOpen}
            footer={null}
            closeIcon={null}
            onCancel={() => modalActions.onClose()}
            style={{ top: 'calc(100vh - 285px)' }}
            className="action-button-modal"
            width={'calc(100vw - 32px)'}
          >
            <ActionMenu
              isFavorite={is_favorite}
              onClickFavorite={handleSetFavorite}
              originalContent={url}
              onClickRead={null}
              onClickTag={null}
              onClickShare={null}
              onClickDlPdf={handleDownloadPdf}
              onClickTranslate={translateAction}
            />
          </Modal>
        </div>
        <Button
          font-weight={400}
          onClick={() => shareModal.onOpen()}
          width="100%"
          label={
            <p className="action-button-label">
              {I18n.t('media.actionMenu.share').toUpperCase()}
            </p>
          }
          icon="share"
          iconLeft
          textColor="rgb(43 61 84)"
          color="#EBECEE"
          fontFamily="Oswald"
          className="action-menu-item"
          style={{ width: 'calc(100vw - 32px)', borderRadius: 24 }}
        />
      </div>
    );
  };

  // Could be moved to a separate component
  const RenderSideMenu = () => {
    if (!post) return;

    return (
      <div className="article-side-menu">
        {!isMobile && RenderCountry()}
        {!isMobile && RenderSource()}
        {RenderTags()}
        {RenderActionMenu()}
        <TagsModal
          articleIds={[post_id]}
          articles={[post]}
          open={tagsModal.isOpen}
          onSubmit={() => {}}
          onClose={() => tagsModal.onClose()}
        />
      </div>
    );
  };

  // Could be moved to a separate component
  const RenderBody = () => {
    const body = get(post, 'content.body');
    const image_url = get(post, 'content.image_url');

    return (
      <div>
        {!!image_url && !body?.includes('<img') && (
          <img src={image_url} alt={`img-article-${post_id}`} />
        )}
        {isHTML(body) ? parseArticle(body) : body}
      </div>
    );
  };

  if (isLoading || isLoadingPost || isLoadingPressHeader) {
    return <Loader />;
  }

  const getAuthor = () => {
    const link = `-  ${I18n.t('media.articleView.from')}`;
    const author = get(post, 'content.author');
    if (author) {
      return `${link} ${author}`;
    }
    const source = get(post, 'content.source.name');
    if (source) {
      return `${link} ${source}`;
    }
    return '';
  };

  return (
    <ArticleViewContainer>
      <Spinner loading={isFetching} />
      <div className="article-container">
        <div className="article-container-header">
          <Link
            to={{
              pathname: '/communication/media/'
            }}
            className="article-container-header-link-back"
          >
            <Button
              type="round"
              icon="dropdown"
              iconColor="rgb(226 93 52)"
              color="rgb(226 93 52 / 20%)"
              onClick={() => {}}
              label={undefined}
              disabled={undefined}
              url={undefined}
            />
          </Link>
          <p className="article-container-header-title">
            {`${moment(post?.content.pubdate).format(
              'DD MMMM YYYY'
            )} ${getAuthor()}`}
          </p>
        </div>
        <div className="article-container-content">
          <div className="article-container-title">
            {isMobile && RenderCountry()}
            <h1>{post?.content.title}</h1>
            {isMobile && RenderSideMenu()}
          </div>
          <div className="article-container-body">{RenderBody()}</div>
        </div>
      </div>
      {!isMobile && RenderSideMenu()}
      {post && (
        <ShareModal
          article={post}
          open={shareModal.isOpen}
          onClose={() => shareModal.onClose()}
        />
      )}
      <Popup
        open={showErrorTranslationModal}
        title="Erreur"
        onClose={() => setShowErrorTranslationModal(false)}
        onOk={() => setShowErrorTranslationModal(false)}
      >
        <span>
          {
            "Une erreur est survenue lors de la traduction de l'article, veuillez réessayer plus tard"
          }
        </span>
      </Popup>
    </ArticleViewContainer>
  );
};

export default ArticleView;
